/**
 * launchFeePaymentService — Phase 10
 *
 * Single entry point for every step of the $99 Launch Fee lifecycle:
 *   - TEST  path: creates a mock payment record server-side and returns.
 *   - LIVE  path (Mainnet):
 *         1. server creates a PENDING payment intent with live SOL amount
 *            and per-treasury SOL allocation (40/40/20)
 *         2. this service builds a single Solana transaction with THREE
 *            SystemProgram.transfer instructions (one per treasury)
 *         3. wallet signs, we send + poll for confirmation
 *         4. server /verify inspects the tx on-chain and marks CONFIRMED
 *
 * Never handles private keys. Signing happens inside the wallet extension.
 * All treasury addresses come from the backend config so the frontend can
 * never redirect funds.
 */
import {
  LAMPORTS_PER_SOL,
  PublicKey,
  SystemProgram,
  Transaction,
} from "@solana/web3.js";

import { paymentService } from "./tokenService";
import {
  LAUNCH_FEE_USD,
  activePaymentMode,
  isLiveMode,
  PAYMENT_MODES,
} from "./launchpadConfig";

const BACKEND_URL =
  typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL
    ? process.env.REACT_APP_BACKEND_URL
    : "";
const PAYMENT_API = `${BACKEND_URL}/api/launchpad/payments`;

// Fallback preview price for TEST mode when the backend is momentarily unreachable.
const FALLBACK_SOL_USD_PREVIEW = 150;

export async function getSolUsdPrice(mode = "LOCALNET_TEST") {
  try {
    const cfg = await paymentService.getPaymentAllocation();
    if (!isLiveMode(mode)) return cfg?.solPricePreview ?? FALLBACK_SOL_USD_PREVIEW;
    // LIVE — the live oracle is server-side. We surface it via the config
    // snapshot so the review UI can show a live SOL preview before signing.
    return cfg?.solPricePreview ?? null;
  } catch {
    return isLiveMode(mode) ? null : FALLBACK_SOL_USD_PREVIEW;
  }
}

export async function calculateLaunchFeeSol(mode = "LOCALNET_TEST", feeUsd = LAUNCH_FEE_USD) {
  const price = await getSolUsdPrice(mode);
  if (!price || price <= 0) return null;
  return Number((feeUsd / price).toFixed(4));
}

/**
 * Build + sign + send the on-chain $99 Launch Fee transfer.
 * Splits the total SOL amount into 40/40/20 across three System transfers.
 * Never runs when LIVE mode is disabled server-side (the intent creation
 * will already have failed with HTTP 503).
 *
 * @param {import("@solana/web3.js").Connection} connection - Mainnet RPC connection
 * @param {{ publicKey: PublicKey, signTransaction: (tx: Transaction) => Promise<Transaction> }} wallet
 * @param {{ paymentId: string, solAllocation: { ecosystem: number, buyback: number, operations: number }, treasuryWallets: { ecosystem: string, buyback: string, operations: string } }} intent
 * @param {(stage: string) => void} onStage
 * @returns {Promise<{ signature: string }>}
 */
async function signAndSendLaunchFee(connection, wallet, intent, onStage = () => {}) {
  if (!wallet?.publicKey || !wallet?.signTransaction) throw new Error("Wallet not connected.");
  if (!intent?.paymentId) throw new Error("Payment intent missing paymentId.");
  const alloc = intent.solAllocation || {};
  const wallets = intent.treasuryWallets || {};
  const roles = ["ecosystem", "buyback", "operations"];
  for (const r of roles) {
    if (!wallets[r]) throw new Error(`Missing treasury address for ${r}.`);
    if (!(alloc[r] > 0)) throw new Error(`Invalid SOL allocation for ${r}.`);
  }

  onStage("Building Launch Fee transaction…");
  const payer = wallet.publicKey;
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash("confirmed");
  const tx = new Transaction({ feePayer: payer, blockhash, lastValidBlockHeight });
  for (const role of roles) {
    tx.add(
      SystemProgram.transfer({
        fromPubkey: payer,
        toPubkey: new PublicKey(wallets[role]),
        lamports: Math.round(Number(alloc[role]) * LAMPORTS_PER_SOL),
      }),
    );
  }

  onStage("Waiting for wallet approval…");
  const signed = await wallet.signTransaction(tx);

  onStage("Sending Launch Fee transaction…");
  const signature = await connection.sendRawTransaction(signed.serialize(), {
    skipPreflight: false,
    preflightCommitment: "confirmed",
    maxRetries: 5,
  });

  // HTTP polling for confirmation (same rationale as tokenService — avoids WSS reliance).
  onStage("Confirming Launch Fee payment…");
  const timeoutMs = 90_000;
  const startedAt = Date.now();
  let confirmed = false;
  let onchainErr = null;
  while (Date.now() - startedAt < timeoutMs) {
    try {
      const res = await connection.getSignatureStatuses([signature], { searchTransactionHistory: false });
      const s = res?.value?.[0];
      if (s?.err) { onchainErr = s.err; break; }
      if (s && (s.confirmationStatus === "confirmed" || s.confirmationStatus === "finalized")) {
        confirmed = true;
        break;
      }
      const h = await connection.getBlockHeight("confirmed");
      if (h > lastValidBlockHeight) break;
    } catch { /* transient — retry */ }
    await new Promise((r) => setTimeout(r, 1200));
  }
  if (onchainErr) throw new Error("Launch Fee transaction failed on-chain: " + JSON.stringify(onchainErr));
  if (!confirmed) throw new Error("Launch Fee transaction not confirmed. Please retry.");
  return { signature };
}

async function verifyLivePayment(paymentId, transactionSignature) {
  if (!paymentId || !transactionSignature) throw new Error("Missing verification params.");
  const r = await fetch(`${PAYMENT_API}/${paymentId}/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ transactionSignature }),
  });
  const text = await r.text().catch(() => "");
  if (!r.ok) throw new Error(`Verification failed (${r.status}): ${text || "unknown"}`);
  try { return JSON.parse(text); } catch { return null; }
}

export const launchFeePaymentService = {
  async config()             { return paymentService.getPaymentAllocation(); },
  async solPreview(mode)     { return calculateLaunchFeeSol(mode); },

  /**
   * Create a payment intent server-side. Returns the DB record.
   * TEST modes: status is immediately TEST_CONFIRMED.
   * LIVE mode:  status is PENDING and includes solAllocation + treasuryWallets.
   */
  async createPaymentIntent({ wallet, networkId, idempotencyKey }) {
    const mode = activePaymentMode(networkId);
    if (isLiveMode(mode)) {
      return paymentService.createLivePayment({ wallet, currency: "SOL", idempotencyKey });
    }
    return paymentService.createTestPayment({ wallet, mode, idempotencyKey });
  },

  /**
   * Full LIVE flow: intent + on-chain transfer + server verification.
   * TokenCreator calls this and then proceeds with token creation once
   * the returned payment is CONFIRMED.
   */
  async runLivePayment({ connection, wallet, networkId, idempotencyKey, onStage = () => {} }) {
    const mode = activePaymentMode(networkId);
    if (!isLiveMode(mode)) throw new Error("runLivePayment is only valid on MAINNET.");
    onStage("Creating payment intent…");
    const intent = await paymentService.createLivePayment({
      wallet: wallet.publicKey.toBase58(),
      currency: "SOL",
      idempotencyKey,
    });
    if (!intent?.paymentId) throw new Error("Server did not return a payment intent.");
    // If server returned an already-CONFIRMED payment via idempotency, skip signing.
    if (intent.status === "CONFIRMED" && intent.transactionSignature) {
      onStage("Payment already confirmed.");
      return intent;
    }
    const { signature } = await signAndSendLaunchFee(connection, wallet, intent, onStage);
    onStage("Verifying payment on-chain…");
    const confirmed = await verifyLivePayment(intent.paymentId, signature);
    return confirmed || { ...intent, status: "CONFIRMED", transactionSignature: signature };
  },

  async verifyTransaction(paymentId, transactionSignature) {
    return verifyLivePayment(paymentId, transactionSignature);
  },
  async status(paymentId)                 { return paymentService.getPaymentStatus(paymentId); },
  async associateMint(paymentId, mint)    { return paymentService.attachMint(paymentId, mint); },
  isLive: (mode) => isLiveMode(mode),
  MODES: PAYMENT_MODES,
};
